import React from 'react';
import { Users, Quote } from 'lucide-react';

/**
 * Premium partner logo grid. Used on /workshops + reusable on landing page, /case-studies.
 * Accepts items with shape: { name, sub, logo, website? }
 */
export function PartnerLogoGrid({ items, testId = 'partner-logo-grid' }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4" data-testid={testId}>
      {items.map((org) => {
        const content = (
          <>
            {org.logo ? (
              <img
                src={org.logo}
                alt={`${org.sub || org.name} logo`}
                className="max-h-16 sm:max-h-20 max-w-full object-contain group-hover:scale-105 transition-transform duration-300"
              />
            ) : (
              <>
                <div className="text-slate-900 font-bold text-base sm:text-lg leading-tight text-center">{org.name}</div>
                {org.sub && <div className="text-slate-600 text-xs leading-snug text-center mt-1">{org.sub}</div>}
              </>
            )}
          </>
        );
        const cls = "group relative h-28 sm:h-32 rounded-xl border border-white/10 bg-white/[0.95] hover:bg-white hover:border-violet-500/40 transition-all duration-300 flex flex-col items-center justify-center p-4 overflow-hidden";
        return org.website ? (
          <a key={`${org.name}-${org.sub || ''}`} href={org.website} target="_blank" rel="noopener noreferrer" className={cls} title={org.sub}>{content}</a>
        ) : (
          <div key={`${org.name}-${org.sub || ''}`} className={cls} title={org.sub}>{content}</div>
        );
      })}
    </div>
  );
}

/**
 * Case study card. Accepts a normalized shape:
 * { id, org, short, logo, title, audience, description, outcome, image, industry, quote, quoteAuthor }
 */
export function CaseStudyCard({ cs }) {
  return (
    <article
      className="group relative rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-br from-white/[0.04] to-white/[0.01] hover:border-cyan-500/40 hover:shadow-2xl hover:shadow-violet-500/10 hover:-translate-y-1 transition-all duration-300 flex flex-col"
      data-testid={`case-study-card-${cs.short || cs.id}`}
    >
      <div className="relative h-44 overflow-hidden">
        <img src={cs.image} alt={cs.org} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent" />
        <div className="absolute top-4 left-4 h-12 px-3 rounded-lg bg-white/95 backdrop-blur-sm border border-white/20 shadow-lg flex items-center justify-center">
          {cs.logo ? (
            <img src={cs.logo} alt={`${cs.org} logo`} className="max-h-8 max-w-[120px] object-contain" />
          ) : (
            <span className="text-slate-900 text-xs font-bold tracking-wide">{cs.short}</span>
          )}
        </div>
        {cs.industry && (
          <div className="absolute top-4 right-4 px-2.5 py-1 rounded-full bg-violet-500/20 backdrop-blur-sm border border-violet-400/40 text-violet-100 text-[10px] font-semibold uppercase tracking-wider">
            {cs.industry}
          </div>
        )}
      </div>

      <div className="p-6 flex flex-col flex-1">
        <h4 className="text-lg sm:text-xl font-bold mb-1.5 leading-tight">{cs.org}</h4>
        {cs.title && <p className="text-violet-300 text-sm font-semibold mb-3">{cs.title}</p>}

        {cs.audience && (
          <div className="flex items-center gap-2 mb-4 text-xs">
            <Users className="h-3.5 w-3.5 text-slate-500" />
            <span className="text-slate-400">{cs.audience}</span>
          </div>
        )}

        {cs.description && (
          <p className="text-sm text-slate-300 leading-relaxed mb-5 flex-1">{cs.description}</p>
        )}

        {cs.outcome && (
          <div className="pt-4 border-t border-white/10">
            <p className="text-xs text-cyan-400 font-bold uppercase tracking-wider mb-1.5">Outcome</p>
            <p className="text-sm text-slate-300 leading-relaxed">{cs.outcome}</p>
          </div>
        )}

        {cs.quote && (
          <div className="mt-4 pt-4 border-t border-white/10">
            <Quote className="h-4 w-4 text-violet-400/50 mb-1" />
            <p className="text-sm text-slate-200 italic leading-relaxed">"{cs.quote}"</p>
            {cs.quoteAuthor && <p className="text-xs text-slate-500 mt-1.5">— {cs.quoteAuthor}</p>}
          </div>
        )}
      </div>
    </article>
  );
}

/**
 * Normalize an API case_studies record into the CaseStudyCard shape.
 */
export function normalizeCaseStudy(cs) {
  return {
    id: cs.id,
    org: cs.org_name,
    short: cs.short_name,
    logo: cs.logo_url || null,
    title: cs.engagement_title,
    audience: cs.audience,
    description: cs.description,
    outcome: cs.outcome,
    image: cs.hero_image || '/images/workshop-training.jpg',
    quote: cs.testimonial_quote,
    quoteAuthor: cs.testimonial_author,
    industry: cs.industry,
    category: cs.workshop_category,
  };
}
